import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import { SheetFilters } from "@/common/components/SheetFilters";
import Table from "@/common/components/Table";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { ReceiptTextIcon } from "lucide-react";
import { maintenanceForwardingsTableColumns } from "../../constants/maintenanceForwardingsTableColumns.constant";
import { useMaintenanceForwardings } from "../../hooks/useMaintenanceForwardings";
import { useMaintenanceForwardingsActualEntryFilterOptions } from "../../hooks/useMaintenanceForwardingsActualEntryFilterOptions";
import { useMaintenanceForwardingsPossibleStatuses } from "../../hooks/useMaintenanceForwardingsPossibleStatuses";
import { MaintenanceForwardingsFiltersTransformer } from "../../services/maintenanceForwardingsFiltersTransformer";
import { MaintenanceForwardingsTransformer } from "../../services/maintenanceForwardingsTransformer";
import { MaintenanceForwardingData, MaintenanceForwardingTableItem } from "../../types/maintenanceForwarding.type";
import ActualEntryCascadeFilter from "./components/ActualEntryCascadeFilter";

const MaintenanceForwardingsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const { data: forwardingStatusOptions } = useMaintenanceForwardingsPossibleStatuses({
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceForwardingsFiltersTransformer.statusOptionsTransformer(),
    }),
  });
  const { data: actualEntryFilters } = useMaintenanceForwardingsActualEntryFilterOptions({
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceForwardingsFiltersTransformer.actualEntryFiltersTransformer(),
    }),
  });

  const actualEntryTree = actualEntryFilters?.tree ?? [];
  const entryTypeFilters = actualEntryFilters?.entryTypeFilters ?? [];

  const {
    data: forwardings,
    meta: forwardingsMeta,
    isFetching: forwardingsIsFetching,
    refetch: refetchForwardings,
    error: forwardingsError = {} as any,
  } = useMaintenanceForwardings({
    filters: {
      page: Number(filterParams.page) || 1,
      status: filterParams.status?.value,
      checkinEntryType: filterParams.checkinEntryType?.value,
      establishmentChoiceEntryType: filterParams.establishmentChoiceEntryType?.value,
      financialReviewEntryType: filterParams.financialReviewEntryType?.value,
      directiveEntryType: filterParams.directiveEntryType?.value,
      fiscalAssessmentEntryType: filterParams.fiscalAssessmentEntryType?.value,
      checkoutEntryType: filterParams.checkoutEntryType?.value,
      actualEntry: filterParams.actualEntry?.value,
    },
    mapper: Mapper.mapWithTransformer<MaintenanceForwardingData[], MaintenanceForwardingTableItem[]>({
      transformer: MaintenanceForwardingsTransformer.tableTransformer(),
    }),
  });

  return (
    <div className="inner-screen gap-6">
      <ListHeader
        Icon={<ReceiptTextIcon className="w-10 h-10" strokeWidth={1.5} />}
        title="Encaminhamentos de manutenção"
      />

      <ErrorMessageWrapper
        className="flex-1"
        statusCode={forwardingsError?.status}
        refetchFunction={refetchForwardings}
      >
        <div className="flex-1 flex flex-col gap-6 overflow-hidden">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ActualEntryCascadeFilter
                  tree={actualEntryTree}
                  value={filterParams.actualEntry}
                  onChange={(actualEntry) => setFilterParams((prev) => ({ ...prev, actualEntry }))}
                />

                <ListFilters
                  filters={[
                    {
                      className: '',
                      dataTestid: 'status',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                      value: filterParams.status,
                      options: [{ label: 'Todos', value: '' }, ...(forwardingStatusOptions || [])],
                      placeholder: 'Status do Encaminhamento',
                    }
                  ]}
                />

                <SheetFilters
                  filters={entryTypeFilters.map((filter) => ({
                    label: filter.label,
                    dataTestid: filter.filterKey,
                    options: [
                      {
                        label: 'Todas',
                        value: '',
                        dataTestid: `${filter.filterKey}_option_all`,
                        selected: !filterParams[filter.filterKey]?.value,
                        onClick: () => setFilterParams((prev) => ({ ...prev, [filter.filterKey]: { label: '', value: '' } })),
                      },
                      ...filter.options.map((option) => ({
                        label: option.label,
                        value: option.value,
                        dataTestid: `${filter.filterKey}_option_${option.value}`,
                        selected: filterParams[filter.filterKey]?.value === option.value,
                        onClick: () => setFilterParams((prev) => ({ ...prev, [filter.filterKey]: option })),
                      })),
                    ],
                  }))}
                />
              </div>
            </div>
          </div>

          <Table
            className="flex-1 overflow-auto max-h-max"
            columns={maintenanceForwardingsTableColumns}
            isLoadingData={forwardingsIsFetching}
            page={filterParams.page || 1}
            rows={forwardings}
            totalPages={forwardingsMeta.total_pages}
            totalCount={forwardingsMeta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
}

export default MaintenanceForwardingsList;
